import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type PageChromeSetters = {
  setHero: (hero: ReactNode) => void;
  setHeroAux: (heroAux: ReactNode) => void;
  /**
   * Overrides the desktop top-bar page label with a page-specific breadcrumb
   * (e.g. "Products › {name}" on the edit page). Pass null to fall back to the
   * active nav item's icon + label.
   */
  setBreadcrumb: (breadcrumb: ReactNode) => void;
};

type PageChromeValues = {
  hero: ReactNode;
  heroAux: ReactNode;
  breadcrumb: ReactNode;
};

// Split into two contexts so that pages which only *set* the chrome do not
// re-render when the chrome *value* changes. If a single context carried both,
// calling setHero() would change the context value and re-render every
// consumer — including the page that just called setHero from an effect, whose
// effect deps (e.g. a fresh `t` from useTranslations) would then refire the
// effect → setHero → render → loop ("Maximum update depth exceeded").
const PageChromeSettersContext = createContext<PageChromeSetters | null>(null);
const PageChromeValuesContext = createContext<PageChromeValues | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [hero, setHero] = useState<ReactNode>(null);
  const [heroAux, setHeroAux] = useState<ReactNode>(null);
  const [breadcrumb, setBreadcrumb] = useState<ReactNode>(null);

  // Stable for the provider's lifetime — useState setters never change identity,
  // so this memo (no deps) is computed once and consumers of the setters context
  // never re-render due to chrome updates.
  const setters = useMemo<PageChromeSetters>(
    () => ({ setHero, setHeroAux, setBreadcrumb }),
    [],
  );
  const values = useMemo<PageChromeValues>(
    () => ({ hero, heroAux, breadcrumb }),
    [hero, heroAux, breadcrumb],
  );

  return (
    <PageChromeSettersContext.Provider value={setters}>
      <PageChromeValuesContext.Provider value={values}>
        {children}
      </PageChromeValuesContext.Provider>
    </PageChromeSettersContext.Provider>
  );
}

/** Setters only — use from pages to inject the desktop hero/heroAux chrome. */
export function usePageChrome(): PageChromeSetters {
  const ctx = useContext(PageChromeSettersContext);
  if (!ctx) {
    throw new Error("usePageChrome must be used within PageChromeProvider");
  }
  return ctx;
}

/** Current chrome values — use from the shell region that renders the chrome. */
export function usePageChromeValues(): PageChromeValues {
  const ctx = useContext(PageChromeValuesContext);
  if (!ctx) {
    throw new Error("usePageChromeValues must be used within PageChromeProvider");
  }
  return ctx;
}
